import "./DynamicTableFilters.css";
import React from "react";
import PropTypes from "prop-types";
import { observer } from "mobx-react";
import map from "lodash/map";
import isEmpty from "lodash/isEmpty";
import classNames from "classnames";
import autobind from "autobind-decorator";
import isFunction from "lodash/isFunction";
import toLower from "lodash/toLower";
import get from "lodash/get";
import Propulator from "higher-order/Propulator";
import CustomForm from "../form/CustomForm";
import SearchFilter from "./filters/SearchFilter";
import CustomTypeaheadInput from "../form/CustomTypeaheadInput";
import history from "util/history";
import { observableMap } from "util/mobx";

@observer
class DynamicTableFilters extends React.Component {
    static propTypes = {
        model: PropTypes.object.isRequired,
        dispatcher: PropTypes.object.isRequired,
        placeholder: PropTypes.string,
        className: PropTypes.string,
        disableUntilLoaded: PropTypes.bool
    };

    static defaultProps = {
        disableUntilLoaded: true
    };

    componentDidMount() {
        // We can't be sure whether the DynamicTableFilters or the DynamicTable will be
        // mounted first. As a result, we bind in both places.
        this.props.model.bindDispatcher(this.props.dispatcher);
        if (this.props.model.enableHistory) {
            this.props.model.parseUrlSearchQuery(window && window.location ? window.location.search : "");
            history.addPopStateListener(e => {
                this.props.model.parseUrlSearchQuery(window && window.location ? window.location.search : "");
            });
        }
    }

    handleFilterChange(filterModel, e) {
        this.props.model.updateFilter(filterModel.key, e.target.value, this.props.dispatcher);
    }

    handleCheckboxChange(filterModel, e) {
        this.props.model.updateFilter(filterModel.key, e.target.checked ? filterModel.checkboxValue : "", this.props.dispatcher);
    }

    @autobind
    handleTypeaheadFilterChange(filterModel, option) {
        const value = get(option, "value", null);
        this.props.model.updateFilter(filterModel.key, value, this.props.dispatcher);
    }

    handleSearchSubmit(filterModel, e) {
        e.preventDefault();
        this.props.model.updateFilter(filterModel.key, filterModel.searchValue, this.props.dispatcher);
    }

    handleSearchClear(filterModel, e) {
        this.props.model.updateFilter(filterModel.key, "", this.props.dispatcher);
    }

    handleTypeaheadBlur(filterModel, e) {
        if (isEmpty(e.target.value)) {
            this.handleTypeaheadFilterChange(filterModel, "");
        }
    }

    renderTypeaheadFilter(filterModel, index, dispatcher) {
        let filterClasses = {
            "typeahead-filter": true
        };

        if (filterModel.className) {
            filterClasses[filterModel.className] = true;
        }

        return (
            <CustomForm key={index} className={classNames(filterClasses)} form={filterModel.typeaheadForm}>
                <CustomTypeaheadInput
                    id={`typeahead-filter-${index}`}
                    fieldName="typeaheadValue"
                    dispatcher={dispatcher}
                    onOptionSelected={option => this.handleTypeaheadFilterChange(filterModel, option)}
                    onBlur={this.handleTypeaheadBlur.bind(this, filterModel)}
                    maxVisible={5}
                    endpoint={filterModel.endpoint}
                    placeholder={filterModel.placeholder}
                    disabled={this.props.disableUntilLoaded && !this.props.model.isLoaded}
                    {...filterModel.definition.typeaheadProps}
                />
            </CustomForm>
        );
    }

    renderSelectFilter(filterModel, index) {
        let filterClasses = {
            "custom-select": true,
            "has-value": !isEmpty(filterModel.value),
            active: false // TODO implement
        };
        filterClasses[toLower(filterModel.columnName)] = true;

        if (filterModel.className) {
            filterClasses[filterModel.className] = true;
        }

        const placeholderOption = (filterModel.placeholder && !isEmpty(filterModel.placeholder))
            ? (<option key={filterModel.placeholder} value="" default={true}>{filterModel.placeholder}</option>)
            : null;
        return (
            <div key={index} className={classNames(filterClasses)}>
                <select
                    key={index}
                    value={filterModel.value}
                    onChange={this.handleFilterChange.bind(this, filterModel)}
                    disabled={this.props.disableUntilLoaded && !this.props.model.isLoaded}
                >
                    {placeholderOption}
                    {map(filterModel.definition.options, (option, optionIndex) => {
                        return (
                            <option key={optionIndex} value={isFunction(option.value) ? option.value() : option.value}>
                                {isFunction(option.label) ? option.label() : option.label}
                            </option>
                        );
                    })}
                </select>
            </div>
        );
    }

    renderCheckboxFilter(filterModel, index) {
        let filterClasses = {
            "custom-checkbox-container": true,
            checked: !isEmpty(filterModel.value)
        };
        filterClasses[toLower(filterModel.columnName)] = true;

        if (filterModel.className) {
            filterClasses[filterModel.className] = true;
        }

        return (
            <div key={index} className={classNames(filterClasses)}>
                <input id={filterModel.key} className="custom-checkbox"
                    type="checkbox" name={filterModel.key}
                    value={filterModel.checkboxValue} checked={!isEmpty(filterModel.value)}
                    onChange={this.handleCheckboxChange.bind(this, filterModel)}
                    disabled={this.props.disableUntilLoaded && !this.props.model.isLoaded}
                />
                <label htmlFor={filterModel.key}>{filterModel.label}</label>
            </div>
        );
    }

    renderSortFilter(filterModel, index) {
        let filterClasses = {
            "sort-select": true
        };

        if (filterModel.className) {
            filterClasses[filterModel.className] = true;
        }

        return (
            <label key={index}>
                <span className="sort-by">Sort by:</span>
                <select
                    value={filterModel.value}
                    className={classNames(filterClasses)}
                    onChange={this.handleSortChange.bind(this, filterModel)}
                    disabled={this.props.disableUntilLoaded && !this.props.model.isLoaded}
                >
                    {map(filterModel.definition.options, (option, optionIndex) => {
                        return (
                            <option key={optionIndex} value={optionIndex}>
                                {isFunction(option.label) ? option.label() : option.label}
                            </option>
                        );
                    })}
                </select>
            </label>
        );
    }

    render() {
        const filters = observableMap.values(this.props.model.filters);
        const filterControlsClasses = classNames("filter-controls", this.props.className);

        return (
            <div className={filterControlsClasses}>
                {
                    map(filters, (filter, index) => {
                        if (filter.type === "search") {
                            return (
                                <SearchFilter
                                    key={`search-${index}`}
                                    filterModel={filter}
                                    tableModel={this.props.model}
                                    disableUntilTableModelLoaded={this.props.disableUntilLoaded}
                                />
                            );
                        } else if (filter.type === "select") {
                            return this.renderSelectFilter(filter, index);
                        } else if (filter.type === "sort") {
                            // not yet fully supported in mobx
                            return this.renderSortFilter(filter, index);
                        } else if (filter.type === "typeahead") {
                            return this.renderTypeaheadFilter(filter, index, this.props.dispatcher);
                        } else if (filter.type === "checkbox") {
                            return this.renderCheckboxFilter(filter, index);
                        }
                        return null;
                    })
                }
            </div>
        );
    }
}

export default Propulator(DynamicTableFilters);
